import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@orgazone/api-envelope/client';
import { formatMoney, formatMinor } from '@orgazone/money-format';
import {
  // ⚠️ `confirm` VIENT D'ICI, JAMAIS DU NAVIGATEUR. Oublié dans cet import, l'appel tombait sur le
  // `window.confirm` natif — celui que la charte interdit (§24.1quater) — qui prend une CHAÎNE : la
  // modale ne se serait jamais affichée et l'objet passé aurait donné « [object Object] ».
  alert, Badge, confirm, Copyable, DataTable, EmptyState, FilterToolbar, Icon, Modal, Picker, showToast, Tooltip, useLatest,
  type ColumnDef, type Selection, type SortState, type TableStatus,
} from '@orgazone/ui-web';
import { ARTICLES_ROW_HEIGHT, noPhotoHatchCss } from '@orgazone/ui-tokens';
import type { ColumnLayout } from '@orgazone/ui-tokens';
import { NouvelArticleSimple } from './article-nouveau';
import { MenuNouvelArticle } from './article-nouveau-menu';
import { AssistantGamme } from './article-gamme';
import { AssistantImport } from './article-import';
import { useAfterImport, type ImportBatch } from './import-job';
import { api, isArticleSortKey, type ArticleFacet, type ArticleFacets, type ArticleModel, type ArticlePageView, type ArticleSortKey, type SecondDimension, type TablePrefs } from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { Absent, Variants, Divergent, Fourchette, Stock, Texte } from './articles-shared';
import { sizeSpanLabel } from './articles-variants-axes';
import { EnvoiArticlesRapides } from './articles-envoi-rapide';
import { ArticleSheet } from './article-fiche';
import { useStoreScope } from '../store-context';
import { currentVocabulary, facetLabel, fieldLabel } from '../vocabulary';

/**
 * ARTICLES — le catalogue du magasin ouvert : la liste en lecture, la fiche en écriture.
 *
 * UTILITÉ : c'est la première tranche verticale du back-office — un écran réel, une route réelle, la
 * vraie base d'un magasin. Tout ce qui s'affiche ici sort de `article` et `stock` de CE magasin ; rien
 * n'est meublé, et ce qui manque se dit.
 *
 * ⚠️ UNE LIGNE = UN MODÈLE, pas une déclinaison. La base stocke une ligne par variante (couleur, taille,
 * gencode) ; le regroupement est fait EN SQL et la pagination porte sur les groupes. Le chevron déplie
 * les déclinaisons réelles du modèle.
 *
 * ⚠️ LE TABLEAU EST CONTRÔLÉ : il ne trie pas, ne pagine pas, ne filtre pas. Chaque intention repart au
 * serveur, qui renvoie la page suivante et le tri APPLIQUÉ. Un tableau qui trierait tout seul aurait
 * déjà chargé les 30 000 lignes.
 *
 * ⚠️ LE CLIC DE LIGNE OUVRE LA FICHE (P9, 2026-08-17), en LECTURE. Le commentaire qui vivait ici disait
 * « pas de clic de ligne : la fiche article n'existe pas » — devenu faux le jour où elle a existé, et
 * une justification fausse coûte plus cher qu'une fonction manquante : elle ferme le sujet.
 *
 * ⚠️ CE QUE CET ÉCRAN N'OFFRE TOUJOURS PAS, ET POURQUOI : pas d'export (la route serveur n'existe
 * pas), pas de colonne Remise. Ce qui n'est pas branché s'enlève, il ne se simule pas (§23.0).
 * ⚠️ « NOUVEL ARTICLE » MONTRE SES TROIS PORTES, dont deux ne sont pas construites (gamme complète,
 * import de fichier). J'avais d'abord câblé le bouton sur la seule porte ouverte, au motif qu'offrir
 * une option non implémentée est un défaut. Raisonnement juste, mauvais endroit : masquer deux portes
 * ne dit pas « pas encore », ça dit « ça n'existe pas » — et on cherche alors une fonction qu'on sait
 * prévue. La politique du dépôt tranche : « zéro bouton mort » = STUB À TOAST EXPLICITE. La porte est
 * visible, son état se lit AVANT le clic (mention « bientôt »), et le clic dit ce qui manque.
 * ⚠️ LES TROIS ACTIONS DE MASSE, elles, ÉCRIVENT RÉELLEMENT (remettre en vente, retirer, changer de
 * famille) : chacune passe par le journal master-data, sous la provenance de la session, et rend le
 * nombre de DÉCLINAISONS touchées. Elles ne sont apparues qu'avec leur exécution — des cases à cocher
 * sans rien à en faire sont une option offerte non implémentée.
 *
 * ⚠️ CORRECTION D'UNE JUSTIFICATION FAUSSE (2026-08-16) : ce commentaire disait « aucune table de
 * promotion dans la base magasin ». C'est faux — `promotion` et `promotion_target` sont dans le tronc
 * partagé (`db-schema/pos.ts`) et le producteur master-data les déclare. Ce qui manque est l'agrégat
 * par modèle ET un arbitrage : les promotions ciblent des CRITÈRES (famille, fournisseur, gencode…),
 * pas des modèles — un modèle dont les déclinaisons ne sont pas toutes ciblées n'a pas « une » remise.
 * Voir `docs/PLAN-Ecran-Articles.md` (D7). Une justification fausse est pire qu'une fonction
 * manquante : elle ferme le sujet.
 */
/**
 * Une durée en secondes, dite comme un humain la dirait.
 * ⚠️ ARRONDIE VERS LE HAUT et jamais au-delà de la minute : « 38 secondes » se lit, « 0,63 minute »
 * non. Annoncer moins que le réel serait pire que ne rien annoncer — l'attente paraîtrait anormale.
 */
const dureeLisible = (s: number): string =>
  (s < 60 ? t('{n} seconde', { n: Math.max(1, s) }) : t('{n} minute', { n: Math.ceil(s / 60) }));

export function Articles() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;

  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  /** Les filtres de dimension, valeur EXACTE par dimension ('' = dimension non filtrée). */
  const [facettes, setFacettes] = useState<Partial<Record<ArticleFacet, string>>>(() => Object.fromEntries(DIMENSIONS.map((d) => [d.key, ''])));
  /** « Tailles 2 uniquement » (maquette « Articles — Tailles 2 ») : les modèles dont la grille porte un second axe. */
  const [axes2, setAxes2] = useState(false);
  /**
   * LE LOT D'UN IMPORT (« Voir les articles ») : la liste ne montre que ce que l'import a écrit, sous sa pastille.
   * ⚠️ JAMAIS MÉMORISÉ : il désigne un geste qui vient d'avoir lieu, pas un réglage du magasin.
   * ⚠️ DOUBLÉ D'UNE RÉFÉRENCE : les filtres mémorisés arrivent APRÈS l'ouverture du lot, et ne doivent pas s'y ajouter
   *    — la lecture des réglages consulte la référence, posée au même instant que l'état.
   */
  const [lot, setLot] = useState<ImportBatch | null>(null);
  const lotRef = useRef<ImportBatch | null>(null);
  const poserLot = useCallback((b: ImportBatch | null) => { lotRef.current = b; setLot(b); }, []);
  /**
   * LE SECOND AXE DE CHAQUE GRILLE, par nom — ce qui permet d'écrire « 38 → 46 × 30/32/34 » sans une
   * requête par ligne. ⚠️ LU UNE FOIS PAR MAGASIN ; s'il ne peut pas l'être, seules les VALEURS
   * manquent : le drapeau vient du serveur, donc la pastille « T×L » annonce quand même les deux axes.
   */
  /**
   * Les grilles SECONDAIRES du magasin, par leur nom — de quoi étaler « × 30/32/34 » dans la colonne.
   * ⚠️ INDEXÉES PAR LA GRILLE DU SECOND AXE, plus par la grille principale : c'est l'ARTICLE qui
   * apparie les deux, donc deux modèles d'une même grille de tailles peuvent n'avoir pas le même
   * second axe — ou n'en avoir aucun.
   */
  const [axesParNom, setAxesParNom] = useState<ReadonlyMap<string, SecondDimension>>(new Map());
  /** Ce que les filtres ont le droit d'offrir — lu UNE fois par magasin, pas à chaque requête. */
  const [offres, setOffres] = useState<ArticleFacets | null>(null);
  const [offresKo, setOffresKo] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  // ⚠️ La taille de page est un ÉTAT, plus une constante : le serveur l'accepte jusqu'à 100 depuis le
  // premier jour et l'écran ne l'offrait pas — l'opérateur qui voulait balayer son catalogue devait
  // tourner 40 pages. Elle repart à la page 1 quand elle change, sinon la page 7 d'un découpage à 25
  // devient une page inexistante d'un découpage à 100.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [layout, setLayout] = useState<ColumnLayout>(DEFAULT_LAYOUT);
  const [data, setData] = useState<ArticlePageView | null>(null);
  const [status, setStatus] = useState<TableStatus>({ phase: 'idle' });
  const [attempt, setAttempt] = useState(0);
  const [creation, setCreation] = useState(false);
  const [gamme, setGamme] = useState(false);
  const [importFichier, setImportFichier] = useState(false);
  // ⚠️ LA LISTE REVIENT EN TÊTE après un import : page 1, tri par défaut — les articles écrits par l'import en premier —,
  //    relue depuis le serveur, jamais complétée à la main. « Voir les articles » l'ouvre sur le LOT, les autres filtres
  //    levés, comme la maquette.
  useAfterImport(
    () => { setSort(DEFAULT_SORT); setPageIndex(0); setAttempt((n) => n + 1); },
    (batch) => {
      poserLot(batch);
      setQ(''); setApplied('');
      setFacettes(restoredFacets({}));
      setAxes2(false);
      setSort(DEFAULT_SORT); setPageIndex(0); setAttempt((n) => n + 1);
    },
  );
  const [menuNeuf, setMenuNeuf] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  /** Les modèles cochés. ⚠️ Vidée après chaque action : garder une sélection sur des lignes qui
   *  viennent de changer d'état ferait recliquer sur un état qu'on ne voit plus. */
  const [selection, setSelection] = useState<Selection>({ kind: 'ids', ids: new Set<string>() });
  /**
   * Le modèle dont la FICHE est ouverte — la ligne ENTIÈRE, pas seulement sa clé.
   * ⚠️ La fiche peint son en-tête immédiatement avec ce qu'on tient déjà en main : un écran d'attente
   * devant une donnée déjà chargée est une lenteur inventée.
   */
  const [sheetOpen, setSheetOpen] = useState<ArticleModel | null>(null);
  /** Les modèles en cours de reclassement — «null« = la modale est fermée. */
  const [reclasse, setReclasse] = useState<Selection | null>(null);
  /** Les modèles en partance vers les articles rapides — «null« = la modale de destination est fermée. */
  const [versRapides, setVersRapides] = useState<string[] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ⚠️ LA PALETTE ⌘K OUVRE UNE FICHE ICI : elle navigue en portant la ligne DÉJÀ chargée (état de
  //    navigation) — on l'ouvre sans re-chercher, puis on EFFACE l'état (replace) pour qu'un retour
  //    arrière ou un rechargement ne rouvre pas une fiche que personne n'a redemandée.
  useEffect(() => {
    const line = (location.state as { ficheDepuisPalette?: ArticleModel } | null)?.ficheDepuisPalette;
    if (!line) return;
    setSheetOpen(line);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- l'état de navigation ne se consomme qu'une fois
  }, [location.state]);
  /** Le jeu rendu est-il FILTRÉ ? (état de la réponse affichée, pas de la frappe en cours) */
  const [filtreRendu, setFiltreRendu] = useState(false);
  /**
   * LES RÉGLAGES SONT-ILS CONNUS ? Tant que non, on n'enregistre RIEN et on ne LIT RIEN.
   *
   * ⚠️ C'ÉTAIT UN `useRef`, ET ÇA COÛTAIT UNE REQUÊTE ENTIÈRE (mesuré le 2026-08-17). Un ref ne
   * redéclenche aucun rendu : la liste partait donc AVANT que les réglages soient relus, avec la taille
   * par défaut (25), puis la préférence arrivait (100) et TOUT repartait. Sur MEN FACTORY, cela faisait
   * deux requêtes de 3,5 s au lieu d'une — et comme elles tournent EN MÊME TEMPS sur six cœurs partagés
   * avec les facettes, chacune ralentissait l'autre. En état, l'attente devient explicite.
   * ⚠️ « Connus » inclut l'ÉCHEC de lecture : n'avoir aucun réglage est l'état normal du premier jour.
   * Attendre une réponse qui ne viendra jamais laisserait l'écran vide pour toujours.
   */
  const [knownSettings, setKnownSettings] = useState(false);
  /** Le dernier enregistrement a-t-il échoué ? La phrase de mémorisation en dépend. */
  const [memoKo, setMemoKo] = useState<string | null>(null);
  /** Des lignes sont-elles RÉELLEMENT à l'écran ? (distingue premier chargement et rechargement). */
  const displays = useRef(false);

  // Le champ se calme avant de partir au serveur : une requête par frappe sur 30 000 lignes agrégées
  // n'est pas une recherche, c'est une rafale. La page revient à 1 dès que le terme change.
  useEffect(() => {
    const t = setTimeout(() => { setApplied(q.trim()); setPageIndex(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  /**
   * LES RÉGLAGES DE LA PERSONNE, relus à l'ouverture.
   * ⚠️ RELUS À CHAQUE CHANGEMENT DE MAGASIN, parce que les FILTRES sont mémorisés par magasin : la
   * disposition suit la personne, le filtre suit le magasin où il a été posé.
   * ⚠️ Un échec ici ne bloque rien et ne se plaint pas : n'avoir aucun réglage est l'état normal du
   * premier jour, et l'écran fonctionne parfaitement sans. Ce qui se DIT, c'est l'échec d'ÉCRITURE
   * (plus bas) — parce que là, une promesse de mémorisation aurait été faite.
   */
  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    setKnownSettings(false);
    api.tablePrefs(TABLE_ID)
      .then((p) => {
        if (!vivant) return;
        if (p.layout) setLayout({ order: p.layout.order, hidden: p.layout.hidden });
        if (p.pageSize) setPageSize(p.pageSize);
        // ⚠️ Un filtre restauré DOIT SE VOIR : il repart dans l'état, donc dans les puces révocables.
        // Restauré en silence, il ferait lire « 12 modèles » comme un catalogue de 12 modèles.
        // ⚠️ SEULES LES DIMENSIONS QUE L'ÉCRAN OFFRE sont restaurées : un ancien filtre mémorisé sur une
        //    dimension retirée (fournisseur, secteur) n'aurait aucune puce pour le révoquer, et il
        //    partirait pourtant avec la portée des écritures de masse.
        const filters = p.filters;
        // ⚠️ SAUF SOUS UN LOT : « Voir les articles » a levé les filtres, et les rétablir montrerait une partie du lot.
        if (filters && !lotRef.current) setFacettes(restoredFacets(filters));
      })
      .catch(() => { /* ⚠️ best-effort ASSUMÉ : sans réglage relu, l'écran ouvre sur ses défauts, ce qui est un état juste. */ })
      .finally(() => { if (vivant) setKnownSettings(true); });
    return () => { vivant = false; };
  }, [gln]);

  /**
   * ENREGISTREMENT, après un temps de calme.
   * ⚠️ JAMAIS AVANT D'AVOIR RELU : enregistrer au premier rendu écraserait le réglage mémorisé par
   * les valeurs par défaut — l'utilisateur perdrait sa disposition en ouvrant l'écran.
   * ⚠️ UN ÉCHEC SE DIT : la charte affiche « mémorisés sur votre profil » ; si l'écriture échoue, la
   * phrase devient un mensonge. Elle est alors remplacée par ce qui s'est réellement passé.
   */
  // ⚠️ SEUL LE DERNIER ENREGISTREMENT DIT SON ISSUE : deux enregistrements qui se croisent afficheraient « réglage non
  //    mémorisé » alors que le dernier a réussi — ou effaceraient l'échec du dernier par la réussite d'un ancien.
  const latestPrefs = useLatest();
  useEffect(() => {
    if (!gln || !knownSettings) return;
    const t = setTimeout(() => {
      const prefs: TablePrefs = {
        layout: { order: [...layout.order], hidden: [...layout.hidden] },
        pageSize,
        // ⚠️ DÉRIVÉE DE `DIMENSIONS`, jamais recopiée. Cette liste-ci énumérait `sect` — retiré des
        // filtres depuis — et ignorait `marq` : un filtre posé à l’écran n’était donc pas mémorisé,
        // et un filtre disparu l’était encore. Deux sources pour la même liste divergent toujours
        // du côté qu’on ne relit pas (forme A du catalogue des défauts).
        filters: Object.fromEntries(
          DIMENSIONS.map((d) => [d.key, facettes[d.key]]).filter(([, v]) => !!v),
        ) as Record<string, string>,
      };
      const isLatest = latestPrefs();
      api.saveTablePrefs(TABLE_ID, prefs)
        .then(() => { if (isLatest()) setMemoKo(null); })
        .catch((e: unknown) => { if (isLatest()) setMemoKo(e instanceof Error ? e.message : String(e)); });
    }, 600);
    return () => clearTimeout(t);
  }, [gln, knownSettings, layout, pageSize, facettes, latestPrefs]);

  // ⚠️ LES OFFRES SE LISENT PAR MAGASIN, PAS PAR REQUÊTE : elles décrivent le catalogue, qui ne change
  // pas parce qu'on tape dans un champ. Les relire à chaque frappe multiplierait par deux le trafic
  // d'un écran ouvert toute la journée.
  useEffect(() => {
    if (!gln) { setOffres(null); return; }
    let vivant = true;
    api.articleFacets()
      .then((f) => { if (vivant) { setOffres(f); setOffresKo(null); } })
      // ⚠️ Un échec ne rend pas des listes vides : un filtre sans option se lirait « ce magasin n'a
      // qu'une famille ». Les pickers restent inertes et DISENT pourquoi.
      .catch((e: unknown) => { if (vivant) { setOffres(null); setOffresKo(e instanceof Error ? e.message : String(e)); } });
    return () => { vivant = false; };
  }, [gln]);

  useEffect(() => {
    // ⚠️ AUCUNE REQUÊTE SANS MAGASIN : `idle` dit « rien n'est parti », ce qui n'est pas « chargement ».
    if (!gln) { setStatus({ phase: 'idle' }); setData(null); return; }
    // ⚠️ NI AVANT DE CONNAÎTRE LA TAILLE DE PAGE. Partir sur le défaut puis recommencer avec la
    // préférence relue, c'est payer DEUX FOIS la requête la plus chère de l'écran — 3,5 s pièce sur un
    // catalogue réel, et les deux se concurrencent. On attend, et l'attente s'AFFICHE (« chargement »)
    // au lieu de se dissimuler derrière un premier résultat qu'on va jeter.
    if (!knownSettings) { setStatus(displays.current ? { phase: 'reloading' } : { phase: 'loading' }); return; }
    let vivant = true;
    // ⚠️ « Rechargement » ne s'affiche que s'il y a VRAIMENT des lignes à l'écran : après un échec, on
    // repart en premier chargement, sinon le tableau annoncerait une mise à jour de lignes absentes.
    setStatus(displays.current ? { phase: 'reloading' } : { phase: 'loading' });
    api.articles({
      // ⚠️ La table rend une clé de tri en `string` (elle sert aussi au journal) : on la CONFRONTE à la
      //    liste que le serveur accepte plutôt que de la lui passer telle quelle. Sans ce filtre, une
      //    colonne dont la clé ne serait pas un tri d'article ferait échouer TOUT le chargement.
      page: pageIndex, size: pageSize, sort: isArticleSortKey(sort.key) ? sort.key : DEFAULT_SORT.key as ArticleSortKey, dir: sort.dir, q: applied,
      // ⚠️ DÉRIVÉES DE `DIMENSIONS`, comme la mémorisation : c’est CETTE ligne qui filtre vraiment.
      // Énumérées à la main, elles restaient sur `sup` — le bouton aurait dit « Marque » et le
      // serveur aurait filtré les fournisseurs, sans une erreur pour le trahir.
      ...Object.fromEntries(DIMENSIONS.map((d) => [d.key, facettes[d.key]])),
      secondAxis: axes2,
      ...(lot ? { importBatch: lot.key } : {}),
    })
      .then((page) => {
        if (!vivant) return;
        displays.current = true;
        setData(page);
        // ⚠️ Le libellé suit le TOTAL, pas la frappe en cours : sinon un nombre mesuré pour une
        // recherche s'affiche, le temps du rechargement, sous le libellé d'une autre.
        // ⚠️ ET IL COMPTE TOUS LES FILTRES (correctif de relecture) : le serveur mesure le total
        // APRÈS les facettes. Réduit à la seule recherche, le drapeau laissait
        // « 120 modèles dans ce magasin » s'afficher à un commerçant qui en a 3 000 et qui venait
        // simplement de choisir une famille.
        setFiltreRendu(applied.length > 0 || Object.values(facettes).some(Boolean) || axes2 || lot !== null);
        // ⚠️ LE TRI AFFICHÉ EST CELUI QUE LE SERVEUR A APPLIQUÉ, pas celui qu'on a demandé. Les deux
        // coïncident aujourd'hui, mais le jour où le serveur bornera ou repliera une clé, l'en-tête
        // fléché annoncerait un classement que les lignes ne suivent pas — le tableau mentirait sans
        // qu'aucune erreur ne soit levée. On recale, et seulement s'il y a divergence (sinon boucle).
        if (page.sort.key !== sort.key || page.sort.dir !== sort.dir) {
          setSort({ key: page.sort.key, dir: page.sort.dir });
        }
        setStatus({ phase: 'ready' });
      })
      .catch((e: unknown) => {
        if (!vivant) return;
        // ⚠️ CANAL ALERTE, message RÉEL, et AUCUNE ligne rendue : garder les précédentes sous une erreur
        // les ferait croire à jour, et un tableau vide ferait passer une panne pour un catalogue vide.
        displays.current = false;
        setData(null);
        // ⚠️ UN PARAMÈTRE REFUSÉ N'EST PAS UNE PANNE. Un 400 (clé de tri inconnue, sens invalide) vient
        // de la DEMANDE, pas de la base : proposer « réessayer » relancerait la même requête refusée,
        // et parler d'une base illisible enverrait chercher une panne qui n'existe pas.
        const refusal = e instanceof ApiError && e.status === 400;
        setStatus({
          phase: 'error',
          message: e instanceof Error ? e.message : String(e),
          detail: refusal
            ? t('Cette demande a été refusée par le serveur : le réglage en cause est nommé ci-dessus. Revenez au tri par défaut pour repartir.')
            : t('Rien n’est affiché tant que la lecture n’a pas abouti : ces lignes viendraient de la base de ce magasin, et elles n’ont pas pu être lues.'),
          // Un refus ne se réessaie pas à l'identique — il se corrige.
          onRetry: refusal ? undefined : () => setAttempt((n) => n + 1),
        });
      });
    return () => { vivant = false; };
  }, [gln, knownSettings, pageIndex, pageSize, sort.key, sort.dir, applied, attempt, facettes, axes2, lot]);

  // Les grilles du magasin, pour le second axe de la colonne « Tailles » — une lecture par magasin.
  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    api.articleSizeGrids()
      .then((r) => { if (vivant) setAxesParNom(new Map(r.dimensions.map((d) => [d.name, { name: d.name, label: d.label, values: d.values }]))); })
      // ⚠️ Pas un échec avalé : la colonne garde le drapeau du serveur, donc la pastille « T×L » reste —
      //    seules les valeurs du second axe manquent, et rien ne prétend le contraire.
      .catch(() => { if (vivant) setAxesParNom(new Map()); });
    return () => { vivant = false; };
  }, [gln]);

  // Le tri repart au SERVEUR et ramène en première page : garder la page courante afficherait la page 3
  // d'un classement qu'on vient de changer.
  const onSortChange = useCallback((next: SortState) => { setSort(next); setPageIndex(0); }, []);

  // ⚠️ TOUT CHANGEMENT DE FILTRE RAMÈNE EN PAGE 1 : rester en page 7 d'un jeu qu'on vient de réduire
  // affiche une page vide qui a l'air d'un catalogue vide.
  const poserFacette = useCallback((key: ArticleFacet, v: string) => {
    setFacettes((p) => ({ ...p, [key]: v }));
    setPageIndex(0);
  }, []);

  /**
   * APPLIQUER L'ACTIVITÉ aux modèles cochés.
   *
   * ⚠️ CE QUI EST RENDU EST CE QUI S'EST PASSÉ : le compte rendu parle de DÉCLINAISONS touchées, pas
   * de lignes cochées — un modèle en porte souvent douze, et annoncer « 3 articles modifiés » quand
   * 36 déclinaisons ont changé d'état serait faux.
   * ⚠️ UN ÉCHEC PASSE PAR LE CANAL ALERTE avec son motif réel, et la liste N'EST PAS rafraîchie : la
   * rafraîchir donnerait l'impression que l'action a abouti.
   * ⚠️ CLÉ DE REJEU par action : un double clic ne doit écrire qu'une fois.
   */
  /**
   * LE FILTRE COURANT, sous la forme que le serveur attend — la SEULE source de la portée.
   *
   * ⚠️ IL EST CONSTRUIT À PARTIR DE L'ÉTAT QUI A PRODUIT L'AFFICHAGE, jamais recomposé à la main au
   * moment du clic : c'est ce qui garantit que l'écriture porte sur ce que l'opérateur regardait.
   * ⚠️ LE TRI ET LA PAGE EN SONT ABSENTS, à dessein : ils changent l'ORDRE et la FENÊTRE, jamais
   * l'ENSEMBLE. Les envoyer laisserait croire qu'on n'écrit que la page visible.
   */
  const filtresCourants = useCallback(
    (): Record<string, string> => scopeFilters({ search: applied, facets: facettes, secondAxis: axes2, importBatch: lot?.key ?? null }),
    [applied, facettes, axes2, lot],
  );

  /**
   * ÉCRIRE SUR TOUT CE QUI CORRESPOND AU FILTRE — mesurer, faire confirmer, écrire, rendre compte.
   *
   * ⚠️ QUATRE TEMPS, ET AUCUN N'EST DÉCORATIF.
   * 1. Le PRÉFLIGHT mesure ce que ça pèse EN DÉCLINAISONS — l'écran, lui, ne compte que des modèles,
   *    et un modèle en porte une ou soixante. C'est aussi lui qui DATE la mesure, côté serveur.
   * 2. La CONFIRMATION montre ce nombre réel et prévient pour les caisses : pendant l'écriture, celles
   *    de ce magasin ne remontent pas leurs ventes. Rien n'est perdu, mais ça ne doit pas se découvrir.
   * 3. L'ÉCRITURE renvoie l'instant du préflight, jamais l'heure du poste.
   * 4. Le COMPTE RENDU dit ce qui s'est passé, y compris ce qui n'a PAS été touché.
   *
   * ⚠️ LE RÉSULTAT VA EN ALERTE, PAS EN TOAST, dès qu'un modèle a été écarté : un toast s'efface, et
   * « 40 modèles n'ont pas été traités » est exactement ce qu'il ne faut pas rater.
   */
  const writeOnScope = useCallback(async (
    sel: Extract<Selection, { kind: 'criteria' }>,
    fields: Record<string, string | boolean | null>,
  ) => {
    const filtres = filtresCourants();
    let mesure;
    try {
      mesure = await api.articlesScopePreflight(filtres);
    } catch (e) {
      await alert({
        title: t('Impossible de mesurer la portée'),
        message: t('{motif}. Rien n’a été modifié.', { motif: e instanceof Error ? e.message : String(e) }),
        tone: 'err',
      });
      return;
    }

    // ⚠️ LE PLAFOND SE DIT AVANT LA CONFIRMATION, avec le chiffre réel et le geste qui débloque.
    if (!mesure.runnable) {
      await alert({
        title: t('Sélection trop large'),
        message: tooWideMessage(mesure.variants, mesure.models, mesure.maxVariants)
          + '\n\n' + t('Affinez le filtre — par famille, saison ou marque — et procédez en plusieurs fois.'),
        tone: 'err',
      });
      return;
    }

    const exclus = [...sel.excluded];
    const ok = await confirm({
      title: t('Appliquer à {n} modèle ?', { n: mesure.models }),
      // ⚠️ DES PHRASES ENTIÈRES MISES BOUT À BOUT, JAMAIS UNE PHRASE COUPÉE. Assembler est légitime
      //    quand chaque morceau est une phrase complète — le compte des modèles épargnés devient donc
      //    SA phrase, au lieu d'une parenthèse dont le pluriel ne s'accordait pas avec celui d'avant.
      // ⚠️ LA DURÉE EST ANNONCÉE PARCE QUE L'ÉCRAN N'A RIEN À MONTRER PENDANT : le serveur écrit en
      // une transaction et ne rend la main qu'à la fin. Sans ce repère, 40 secondes de silence font
      // croire à un blocage — et on relance, ce qui n'est PAS un rejeu : la clé est neuve.
      message: t('{n} déclinaison sera modifiée, en {duree} environ.', { n: mesure.variants, duree: dureeLisible(mesure.estimatedSeconds) })
        + (exclus.length ? ` ${t('{n} modèle décoché sera épargné.', { n: exclus.length })}` : '')
        + `\n\n${t('Ne relancez pas si l’écran semble figé : l’opération est en cours, et la lancer deux fois l’appliquerait deux fois.')}`
        + `\n\n${t('Pendant l’écriture, les caisses de ce magasin ne remontent pas leurs ventes ; elles rattrapent ensuite, rien n’est perdu. Préférez une heure creuse si l’opération est large.')}`,
      confirmLabel: t('Appliquer'),
    });
    if (!ok) return;

    try {
      const r = await api.articlesScopeWrite(
        { filters: filtres, excluded: exclus, asOf: mesure.asOf },
        fields,
        `scope-${crypto.randomUUID()}`,
      );
      setSelection({ kind: 'ids', ids: new Set<string>() });
      setAttempt((n) => n + 1);
      // ⚠️ CE QUI N'A PAS ÉTÉ TOUCHÉ SE DIT, ET SE DIT DE FAÇON BLOQUANTE. Sans ça l'opérateur croirait
      // avoir traité un ensemble qu'il n'a jamais vu — le contraire exact de ce que la garde protège.
      if (r.drifted > 0) {
        await alert({
          title: t('Appliqué, mais pas à tout'),
          message: writtenSummary(r.variants, r.models)
            + '\n\n'
            + t('{n} modèle a changé entre l’affichage et le clic : il n’a PAS été modifié, pour ne pas écrire sur des lignes que vous n’aviez pas vues. Relancez l’opération si vous le vouliez aussi.', { n: r.drifted }),
          // ⚠️ `info` ET NON `err` : rien n'a échoué — l'opération a fait exactement ce qu'on lui
          // demandait, sur le sous-ensemble intact. Ce qui doit alerter, c'est ce qui n'a PAS été
          // touché, et c'est le TITRE et le chiffre qui le portent. Peindre une erreur ici enverrait
          // chercher une panne qui n'existe pas.
          tone: 'info',
        });
      } else {
        showToast(
          r.variants === 0
            ? t('Aucune déclinaison touchée : la sélection ne correspond plus à rien.')
            : writtenSummary(r.variants, r.models),
        );
      }
    } catch (e) {
      await alert({
        title: t('Opération de masse impossible'),
        message: t('{motif}. Rien n’a été modifié.', { motif: e instanceof Error ? e.message : String(e) }),
        tone: 'err',
      });
    }
  }, [filtresCourants]);

  const appliquerActivite = useCallback(async (sel: Selection, active: boolean) => {
    // ⚠️ LA PORTÉE « TOUT LE FILTRE » N'EST PAS ENCORE EXÉCUTABLE, ET ÇA SE DIT. Le code rendait ici un
    // tableau vide puis sortait : l'opérateur aurait confirmé « Retirer ces modèles de la vente ? » et
    // RIEN ne se serait passé, sans un mot. Le tableau ne l'offre pas aujourd'hui (le drapeau
    // `allowSelectAllMatching` n'est pas posé), donc personne ne peut l'atteindre — mais le jour où on
    // le posera, la panne serait à la ligne près. On refuse en le nommant, plutôt que de compter sur
    // une garde d'affichage située dans un autre fichier (§23.0).
    if (sel.kind === 'criteria') {
      await writeOnScope(sel, { isActive: active });
      return;
    }
    const cles = [...sel.ids];
    if (cles.length === 0) return;
    try {
      // ⚠️ Clé d'ENVOI et non de contenu : activer, désactiver, puis RÉACTIVER rejouait la clé du
      // premier envoi — la troisième action était ignorée en silence (défaut du 2026-08-17).
      const r = await api.setArticlesActive(cles, active, `act-${crypto.randomUUID()}`);
      setSelection({ kind: 'ids', ids: new Set<string>() });
      setAttempt((n) => n + 1); // relit la page : les lignes ont changé d'état
      showToast(
        r.variants === 0
          ? t('Aucune déclinaison touchée : ces modèles n’ont pas de code-barres, ou ils ont disparu du catalogue.')
          : (active
            ? t('{n} déclinaison remise en vente.', { n: r.variants })
            : t('{n} déclinaison retirée de la vente.', { n: r.variants })),
      );
    } catch (e) {
      await alert({
        title: active ? t('Remise en vente impossible') : t('Retrait impossible'),
        message: t('{motif}. Rien n’a été modifié.', { motif: e instanceof Error ? e.message : String(e) }),
        tone: 'err',
      });
    }
  }, [writeOnScope]);

  /**
   * RECLASSER les modèles cochés — même compte rendu, même canal d'échec que l'activation.
   * ⚠️ Rendue SÉPARÉMENT de la modale : la modale demande, l'écran écrit. Une modale qui appelle le
   * serveur elle-même devrait aussi savoir vider la sélection et relire la page.
   */
  const appliquerFamille = useCallback(async (target: Selection, famille: string) => {
    // La portée par critère a son propre chemin : préflight, avertissement caisses, compte rendu.
    if (target.kind === 'criteria') { await writeOnScope(target, { family: famille }); return; }
    const cles = [...target.ids];
    // ⚠️ Même défaut, même correctif : reclasser en MAILLES puis REVENIR à POLO rejouait la clé du
    // premier envoi, et le reclassement de retour n'était jamais écrit.
    const r = await api.setArticlesFamily(cles, famille, `fam-${crypto.randomUUID()}`);
    setSelection({ kind: 'ids', ids: new Set<string>() });
    setAttempt((n) => n + 1);
    showToast(
      r.variants === 0
        ? t('Aucune déclinaison touchée : ces modèles n’ont pas de code-barres, ou ils ont disparu du catalogue.')
        : t('{n} déclinaison reclassée en « {famille} ».', { n: r.variants, famille }),
    );
  }, [writeOnScope]);

  /** Efface TOUT : les trois dimensions et la recherche. */
  const toutEffacer = useCallback(() => {
    setFacettes(restoredFacets({}));
    setQ('');
    setAxes2(false);
    poserLot(null);
    setPageIndex(0);
  }, [poserLot]);

  // ⚠️ LE VOCABULAIRE EST POSÉ AVEC LA VUE : le contexte magasin l'écrit avant de commiter, mais ce mémo
  //    se calcule au premier rendu, avant lui, et rien ne le lierait au mot du magasin — il garderait le
  //    filet (« Saison ») jusqu'à un recalcul accidentel. Un mémo calculé avant le vocabulaire doit être
  //    invalidé par lui : la table en vigueur entre dans ses dépendances.
  const vocabulary = currentVocabulary();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `buildColumns` lit le vocabulaire par `fieldLabel`, un singleton de module que la règle ne voit pas
  const columns = useMemo(() => buildColumns(axesParNom), [axesParNom, vocabulary]);
  const rows = data?.rows ?? [];

  // ⚠️ CHAQUE FILTRE APPLIQUÉ PORTE SA PUCE, sans exception : un filtre qu'on ne voit pas fait lire
  // « 12 articles » comme un catalogue de 12 articles.
  const puces = [
    ...(applied ? [{ id: 'q', label: t('Recherche : « {q} »', { q: applied }), onRemove: () => setQ('') }] : []),
    ...DIMENSIONS.filter((d) => facettes[d.key]).map((d) => ({
      id: d.key,
      label: `${facetLabel(d.key)} : ${facettes[d.key]}`,
      onRemove: () => poserFacette(d.key, ''),
    })),
    ...(axes2 ? [{ id: 'axes', label: t('Tailles 2 uniquement'), onRemove: () => { setAxes2(false); setPageIndex(0); } }] : []),
    ...(lot ? [{ id: 'lot', label: lotLabel(lot), onRemove: () => { poserLot(null); setPageIndex(0); } }] : []),
  ];
  const filtresPoses = puces.length > 0;

  // ⚠️ QUATRE SITUATIONS SANS TABLEAU, jamais confondues — c'est la façon la plus courante dont une
  // liste ment : contexte en cours de lecture · contexte ILLISIBLE (une panne) · aucun magasin ouvert au
  // compte · aucun magasin choisi. Les fondre en un seul « aucun article » ferait passer une panne
  // d'infrastructure pour un catalogue vide.
  if (!gln) {
    return (
      <>
        <Heading total={null} filtre={false} />
        <div className="dcard" style={{ padding: 0, overflow: 'hidden' }}>
          {scope.loading && (
            <EmptyState icon="store" title={t('Lecture du contexte…')} note={t('Le back-office cherche quel magasin cette session administre.')} />
          )}
          {!scope.loading && scope.error && (
            <div role="alert" style={{ padding: 24 }}>
              <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--danger-ink)', marginBottom: 6 }}>
                
                {t('Contexte magasin illisible')}
              </div>
              <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
                
                {t('Le magasin de cette session n’a pas pu être lu : {error}. Aucun article n’est affiché — ce n’est pas un catalogue vide, c’est une lecture qui n’a pas abouti.', { error: scope.error })}
              </p>
            </div>
          )}
          {!scope.loading && !scope.error && (
            <EmptyState
              icon="store"
              title={scope.view && scope.view.stores.length === 0 ? t('Aucun magasin ouvert à ce compte') : t('Aucun magasin actif')}
              note={
                scope.view && scope.view.stores.length === 0
                  ? t('Aucun magasin n’est ouvert à ce compte : un administrateur doit accorder l’accès magasin par magasin. Sans magasin, il n’y a aucune base à lire.')
                  : t('Choisissez le magasin à administrer dans la barre du haut. Le back-office ouvre la base de CE magasin — aucun écran n’affiche plusieurs magasins à la fois.')
              }
            />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Heading
        total={data?.total ?? null}
        filtre={filtreRendu}
        menu={<MenuNouvelArticle open={menuNeuf} onOpenChange={setMenuNeuf} onChoisir={(p) => { if (p === 'simple') setCreation(true); else if (p === 'gamme') setGamme(true); else if (p === 'import') setImportFichier(true); }} />}
      />

      <NouvelArticleSimple
        open={creation}
        onClose={() => setCreation(false)}
        offres={offres}
        // ⚠️ La liste se RELIT depuis le serveur : ajouter la ligne a la main l afficherait avant
        //    que le catalogue ne la porte, et un refus la laisserait a l ecran.
        onCree={() => setAttempt((n) => n + 1)}
      />

      <AssistantGamme
        open={gamme}
        onClose={() => setGamme(false)}
        offres={offres}
        // ⚠️ MÊME RÈGLE QUE LA CRÉATION SIMPLE : la liste se RELIT depuis le serveur. Ajouter les
        //    lignes à la main les afficherait avant que le catalogue ne les porte.
        onCree={() => setAttempt((n) => n + 1)}
      />

      <AssistantImport open={importFichier} onClose={() => setImportFichier(false)} />
      <DataTable<ArticleModel>
        tableId={TABLE_ID}
        // ⚠️ 56 EST LE RELEVE (maquette l.921), pas un confort : la rangee porte une VIGNETTE de
        // 34 px. A 44, il ne reste que 5 px au-dessus et en dessous — l image touche presque le
        // filet. La densite continue de piloter : elle met ce releve a l echelle.
        rowHeight={ARTICLES_ROW_HEIGHT}
        unit={{ one: t('modèle'), gender: 'm' }}
        columns={columns}
        rows={rows}
        getRowId={(r) => r.modelKey}
        status={status}
        sort={sort}
        onSortChange={onSortChange}
        defaultSort={DEFAULT_SORT}
        page={{ index: data?.page.index ?? pageIndex, size: data?.page.size ?? pageSize, total: data?.total ?? 'unknown' }}
        onPageChange={setPageIndex}
        onPageSizeChange={(n) => { setPageSize(n); setPageIndex(0); }}
        /* ⚠️ Un modèle dont AUCUNE déclinaison n'est active reste dans la liste (l'historique de vente
           le référence), mais il se lit autrement : la teinte de mise à l'écart dit « ce n'est plus
           vendable » à l'échelle de la ligne entière, là où le badge le disait à l'échelle du mot. */
        rowTone={(r) => (r.activeCount === 0 ? 'muted' : undefined)}
        /* ⚠️ LE CLIC OUVRE LA FICHE. Les trois zones déjà cliquables de la rangée l'annulent, et
           chacune arrête la propagation à SA source : la case (composant du tableau), le chevron de
           dépliage (idem), et le bouton de copie du code (`Copyable`, corrigé dans le paquet — chez
           l'appelant, le premier écran qui l'oublierait livrerait le défaut). */
        onRowActivate={(row) => setSheetOpen(row)}
        /* ⚠️ LA SÉLECTION N'ARRIVE QUE MAINTENANT, et c'est délibéré : le kit sait la rendre depuis le
           premier jour, mais aucune action n'était exécutable — des cases à cocher sans rien à en
           faire sont une option offerte non implémentée (§23.0). Elles arrivent AVEC leurs actions.
           ⚠️ `allowSelectAllMatching` EST POSÉ EN DERNIER, et l'ordre était la règle : la route qui
           reçoit un CRITÈRE d'abord, son préflight ensuite, le drapeau seulement une fois les deux en
           place. Posé avant, il aurait offert un geste qui ne se serait pas fait — et le code sortait
           alors en silence. Ce commentaire disait « PAS de allowSelectAllMatching » ; il est devenu
           faux le jour où la route a existé, et une justification périmée ferme le sujet. */
        selection={{ value: selection, onChange: setSelection, allowSelectAllMatching: true }}
        bulkActions={[
          {
            /* ⚠️ PREMIÈRE DES ACTIONS, comme le relevé : c'est le geste qui ferme la boucle entre le
               catalogue — où on cherche et compare — et la grille d'accueil de la caisse. */
            id: 'quick',
            label: t('+ Articles rapides'),
            /* ⚠️ « TOUT LE FILTRE » EST REFUSÉ, AVEC SA RAISON. Les articles rapides sont une poignée
               de favoris : envoyer un filtre entier poserait des milliers de tuiles qu'il faudrait
               ensuite retirer une par une. Le refus se dit sur le bouton plutôt que de laisser
               découvrir la limite après le clic (§23.0). */
            disabled: (sel) => (sel.kind === 'criteria'
              ? t('Les articles rapides se choisissent un par un : cochez les modèles voulus plutôt que tout le filtre.')
              : false),
            /* ⚠️ PAS de `confirm` : la modale de destination QUI SUIT porte la question, et mieux —
               elle montre le nombre réel de déclinaisons et ce que chaque disposition y gagnerait. */
            run: (sel) => { if (sel.kind === 'ids' && sel.ids.size) setVersRapides([...sel.ids]); },
          },
          {
            id: 'activer',
            label: t('Remettre en vente'),
            confirm: {
              title: t('Remettre ces modèles en vente ?'),
              message: t('Toutes leurs déclinaisons redeviennent vendables en caisse à la prochaine synchronisation.'),
              confirmLabel: t('Remettre en vente'),
            },
            run: (sel) => appliquerActivite(sel, true),
          },
          {
            id: 'retirer',
            label: t('Retirer de la vente'),
            tone: 'danger',
            confirm: {
              title: t('Retirer ces modèles de la vente ?'),
              message: t('Toutes leurs déclinaisons cessent d’être vendables en caisse. Rien n’est supprimé : l’historique de vente les référence, et ils restent au catalogue.'),
              confirmLabel: t('Retirer de la vente'),
            },
            run: (sel) => appliquerActivite(sel, false),
          },
          {
            id: 'famille',
            label: t('Changer de famille'),
            /* ⚠️ PAS de `confirm` ici, et ce n'est pas un oubli : la modale QUI SUIT porte la question,
               et elle la porte mieux — elle montre le nombre touché ET la famille choisie. Deux
               questions d'affilée feraient valider à l'aveugle celle qui compte. */
            run: (sel) => { if (sel.kind === 'criteria' || sel.ids.size) setReclasse(sel); },
          },
        ]}
        columnLayout={layout}
        onColumnLayoutChange={(next) => setLayout(next)}
        reorderableColumns
        /* ⚠️ LA PHRASE EST VRAIE DEPUIS P5, et elle cesse de l'être si l'écriture échoue : elle dit
           alors ce qui s'est réellement passé. Une promesse de mémorisation non tenue se lit comme un
           bug, pas comme une absence. */
        columnPersistenceNote={memoKo
          ? t('Le dernier réglage n’a PAS pu être mémorisé : {memoKo}', { memoKo })
          : t('Mémorisés sur votre profil : vous les retrouvez sur tous vos postes. Les filtres, eux, restent attachés à ce magasin.')}
        expandable={{
          expandedIds: expanded,
          onToggle: (id) => setExpanded((prev) => {
            const next = new Set(prev);
            if (!next.delete(id)) next.add(id);
            return next;
          }),
          render: (row) => <Variants modelKey={row.modelKey} />,
        }}
        toolbar={
          // Géométrie de la maquette : gouttière 9, le champ prend la place restante, les filtres
          // suivent. ⚠️ SUR MOBILE, seule la RECHERCHE reste en barre — les filtres passent en tiroir
          // (`FilterToolbar`, décision utilisateur 2026-08-24) avec le compte des actifs.
          <FilterToolbar
            actifs={puces.length}
            title={t('Filtres du catalogue')}
            search={
              /* ⚠️ CAPSULE AVEC LOUPE — recette `.field-search` de la charte, relevée sur la maquette.
                  L'écran posait un `.field-input` à coins arrondis, sans loupe et à largeur fixe. */
              <div className="field-search">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
                </svg>
                <input
                  // ⚠️ PAS `type="search"` : le navigateur y ajoute SA croix d'effacement et sa propre
                  // métrique — deux éléments que la maquette ne dessine pas, dans une capsule étroite.
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  /* ⚠️ Le placeholder promet « code-barres » ET la recherche serveur couvre réellement le
                     gencode : la maquette promettait sans tenir, et une douchette dans ce champ ne trouvait
                     rien — le commerçant en concluait que son article était absent. */
                  placeholder={t('Désignation, code, code-barres, marque…')}
                  aria-label={t('Rechercher un article')}
                />
              </div>
            }
            filters={<>
          {DIMENSIONS.map((d) => (
            <Picker
              key={d.key}
              id={`bo-articles-${d.key}`}
              label={facetLabel(d.key)}
              allLabel={t(d.tout)}
              options={(offres?.values[d.key] ?? []).map((v) => ({ value: v.value, label: v.value }))}
              // ⚠️ La recherche part au SERVEUR : la liste d ouverture n est plus une limite, juste un
              // point de depart. Une valeur au-dela reste atteignable en la tapant.
              onSearch={(t) => api.articleFacetSearch(d.key, t).then((r) => r.map((v) => ({ value: v.value, label: v.value })))}
              loading={!offres && !offresKo}
              // Les valeurs n'ont pas pu être lues : le filtre est INERTE, pas vide — un référentiel
              // vide se lirait « ce magasin n'a qu'une famille ».
              unavailable={Boolean(offresKo)}
              value={facettes[d.key] ?? ''}
              onChange={(v) => poserFacette(d.key, v)}
            />
          ))}
          {/* ⚠️ LA BASCULE « TAILLES 2 UNIQUEMENT » de la maquette « Articles — Tailles 2 » : un bouton à
              état, teal quand il est posé. Il FILTRE AU SERVEUR (comme le stock), jamais la page reçue. */}
          <button
            type="button"
            aria-pressed={axes2}
            onClick={() => { setAxes2((v) => !v); setPageIndex(0); }}
            title={t('Ne garder que les modèles dont la grille porte un second axe (longueur, bonnet…)')}
            style={{
              height: 36, padding: '0 12px', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontWeight: 600,
              whiteSpace: 'nowrap', cursor: 'pointer',
              border: `1px solid ${axes2 ? 'var(--teal-500)' : 'var(--ink-200)'}`,
              background: axes2 ? 'var(--teal-100)' : 'var(--paper)',
              color: axes2 ? 'var(--teal-800)' : 'var(--ink-500)',
            }}
          >{t('Tailles 2 uniquement')}</button>
          {/* ⚠️ « Effacer les filtres » EST DANS LA BARRE, en lien teal, et n'apparaît que si un filtre
              est posé — recette de la maquette. Le tableau en offre un second dans son état vide ;
              celui-ci est celui qu'on voit sans avoir à vider la liste d'abord. */}
          {filtresPoses && (
            <button
              type="button"
              onClick={toutEffacer}
              style={{
                height: 36, padding: '0 12px', border: 'none', borderRadius: 'var(--r-ctrl)',
                background: 'transparent', color: 'var(--teal-700)', fontSize: '12.5px',
                fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
              }}
            >{t('Effacer les filtres')}</button>
          )}
          {/* ⚠️ L'ÉCHEC DE LECTURE DES OFFRES SE DIT : sans ça, quatre filtres inertes passent pour
              un catalogue sans classement. */}
          {offresKo && (
            <span role="alert" style={{ fontSize: 'var(--t-meta)', color: 'var(--danger-ink)', maxWidth: 260, lineHeight: 1.4 }}>
              
              {t('Valeurs de filtre illisibles : {offresKo}', { offresKo })}
            </span>
          )}
          {/* ⚠️ LA PASTILLE DU LOT, recette de la maquette : teal, le nom du fichier et ses modèles, une croix qui rend
              le catalogue entier. */}
          {lot && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', height: 36, borderRadius: 'var(--r-pill)', maxWidth: '100%',
              background: 'var(--teal-100)', border: '1px solid var(--teal-400)', color: 'var(--teal-800)',
            }}>
              <span style={{ padding: '0 4px 0 13px', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {lotLabel(lot)}
              </span>
              <Tooltip content={t('Revoir tout le catalogue')}>
                <button
                  type="button"
                  aria-label={t('Retirer ce filtre')}
                  onClick={() => { poserLot(null); setPageIndex(0); }}
                  style={{
                    width: 30, height: 34, flexShrink: 0, border: 'none', borderRadius: '0 var(--r-pill) var(--r-pill) 0',
                    background: 'transparent', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <Icon name="x" size={11} stroke={2.6} />
                </button>
              </Tooltip>
            </span>
          )}
            </>}
          />
        }
        /* ⚠️ `filtersActive` DÉCLARÉ, jamais déduit : sans lui, un catalogue neuf annoncerait « les
           filtres actifs ne ramènent aucune ligne » et proposerait d'effacer des filtres fantômes. */
        filtersActive={filtresPoses}
        filterChips={puces}
        onClearFilters={toutEffacer}
        emptyStates={{
          // ⚠️ ÉTAT QUE LA MAQUETTE N'A JAMAIS VU : elle a toujours des données. C'est pourtant le premier
          // que rencontre un magasin fraîchement provisionné, dont le catalogue n'est pas encore descendu.
          empty: {
            icon: 'tag',
            title: t('Aucun article dans ce magasin'),
            note: t('La base de ce magasin ne contient aucun article. Le catalogue arrive par l’EDI fournisseur, par un import de fichier ou par la reprise — il n’y a rien à créer depuis cet écran, qui est en lecture.'),
          },
          noMatch: {
            title: t('Aucun article ne correspond'),
            note: t('Élargissez la recherche ou effacez les filtres.'),
          },
        }}
      />
      {/* ⚠️ MONTÉES HORS DU TABLEAU : les modales survivent au rechargement de la liste. */}
      <ArticleSheet
        line={sheetOpen}
        /* ⚠️ CE QU'ON A DÉJÀ EN MAIN NE SE REDEMANDE PAS : la barre de filtres a chargé ces valeurs,
           et les redemander coûtait à la fiche la totalité de son délai d'ouverture (≈800 ms). */
        offresInitiales={offres}
        onClose={() => setSheetOpen(null)}
        /* ⚠️ LA PAGE SE RELIT APRÈS UNE ÉCRITURE : les lignes viennent de changer, et un tableau qui
           garde l’ancienne valeur pendant que la fiche montre la nouvelle est un écran qui se
           contredit lui-même. */
        onChanged={() => setAttempt((n) => n + 1)}
      />
      <ReclasserModal
        target={reclasse}
        familles={(offres?.values.fam ?? []).map((v) => v.value)}
        onClose={() => setReclasse(null)}
        onApply={appliquerFamille}
      />
      {versRapides && (
        <EnvoiArticlesRapides
          modelKeys={versRapides}
          onClose={() => setVersRapides(null)}
          onOrganiser={(layoutId) => {
            setSelection({ kind: 'ids', ids: new Set<string>() });
            navigate(`/articles-rapides?layout=${encodeURIComponent(layoutId)}`);
          }}
        />
      )}
    </>
  );
}

/**
 * CHANGER DE FAMILLE — la modale qui pose la question et attend la réponse du serveur.
 *
 * ⚠️ ELLE NE SE FERME PAS AVANT D'AVOIR ÉCRIT. Fermer d'abord serait le mensonge type : l'opérateur
 * verrait la liste revenir comme si c'était fait, et un échec arriverait sur un écran qui a déjà tourné
 * la page. Pendant l'écriture, la fermeture est REFUSÉE (`blockClose`) et le pied le dit.
 *
 * ⚠️ AUCUNE FAMILLE NOUVELLE ne se crée ici : on ne propose que ce que le catalogue porte déjà, et la
 * modale l'écrit. Une saisie libre ferait naître « chaussure », « Chaussure » et « chaussures » comme
 * trois familles — et personne ne pourrait plus filtrer.
 */
function ReclasserModal({ target, familles, onClose, onApply }: {
  target: Selection | null;
  familles: string[];
  onClose: () => void;
  onApply: (target: Selection, famille: string) => Promise<void>;
}) {
  const [famille, setFamille] = useState('');
  const [writing, setWriting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // Chaque ouverture repart à zéro : garder le choix précédent ferait reclasser un autre lot dans une
  // famille qu'on n'a pas choisie pour lui.
  useEffect(() => { if (target) { setFamille(''); setFailure(null); setWriting(false); } }, [target]);

  const validate = async () => {
    if (!target || !famille || writing) return;
    setWriting(true);
    setFailure(null);
    try {
      await onApply(target, famille);
      onClose();
    } catch (e) {
      // ⚠️ L'ÉCHEC RESTE DANS LA MODALE, avec son motif réel : la refermer perdrait la sélection ET la
      // famille choisie, donc obligerait à tout recommencer pour lire l'erreur ailleurs.
      setFailure(e instanceof Error ? e.message : String(e));
      setWriting(false);
    }
  };

  // ⚠️ UNE PORTEE PAR CRITERE N'EST PAS ENUMERABLE : son compte vient du tableau, pas d'une longueur.
  const n = target?.kind === 'ids' ? target.ids.size : (target?.total ?? 0);
  const parCritere = target?.kind === 'criteria';
  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      blockClose={() => writing}
      title={t('Changer de famille')}
      subtitle={parCritere
        // ⚠️ LE NOMBRE EXACT N'EST PAS ENCORE CONNU ICI : il vient du préflight, juste après la
        // validation. Annoncer une valeur d'écran serait annoncer un chiffre qu'on n'a pas mesuré.
        ? t('{n} modèles correspondent au filtre — le décompte exact des déclinaisons est vérifié avant d’écrire.', { n })
        : t('{n} modèle sélectionné — toutes leurs déclinaisons seront reclassées.', { n })}
      icon="tag"
      tone="teal"
      width="sm"
      footer={
        <>
          {/* Le motif de refus se lit SUR le bouton, jamais dans un bouton gris et muet. */}
          {!famille && !writing && (
            <span style={{ marginRight: 'auto', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
              
              {t('Choisissez la famille d’arrivée.')}
            </span>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <button type="button" className="btn-cta" onClick={validate} aria-disabled={!famille || writing}>
            {writing ? t('Reclassement…') : t('Reclasser')}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Picker
          id="bo-articles-reclasser"
          /* ⚠️ Habillage `field` : dans une modale, la maquette pose un CHAMP (40 px, pleine largeur,
             intitulé au-dessus) et non le déclencheur de barre d'outils — et elle n'y offre jamais de
             rangée « tout », qui reviendrait à « dé-choisir » la famille. */
          variant="field"
          label={t('Famille d’arrivée')}
          allLabel={t('Choisir une famille…')}
          options={familles.map((v) => ({ value: v, label: v }))}
          onSearch={(t) => api.articleFacetSearch('fam', t).then((r) => r.map((v) => ({ value: v.value, label: v.value })))}
          value={famille}
          onChange={setFamille}
        />
        <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          
          {t('Seules les familles déjà présentes au catalogue sont proposées. Créer une famille se fera depuis la fiche article.')}
        </p>
        {failure && (
          <p role="alert" style={{ margin: 0, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--danger-ink)', lineHeight: 1.5 }}>
            {t('{failure}. Rien n’a été modifié.', { failure })}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Les quatre dimensions filtrables, et leurs libellés.
 * ⚠️ MÊMES CLÉS QUE LE SERVEUR (`ARTICLE_FACETS`) : ce sont elles qui voyagent en query, et le serveur
 * n'accepte que celles-là. Une clé inventée ici serait simplement ignorée — donc un filtre sans effet.
 */
/**
 * Les dimensions filtrables : famille, MARQUE, saison.
 *
 * ⚠️ ÉCART ASSUMÉ AVEC LA MAQUETTE, tranché par l'utilisateur (2026-08-20) : elle filtre sur le
 * FOURNISSEUR, on filtre sur la MARQUE. C'est ce qu'on cherche en rayon — un client demande du
 * Levi’s, pas « ce qui vient de chez Bestseller ». Le fournisseur n'est plus non plus une colonne
 * (décision utilisateur 2026-10-08) : c'est un GROUPE, que l'article ne porte presque jamais — mesuré,
 * 379 291 articles sur 379 682 ont une marque et aucun un fournisseur. Il ne s'affiche que dans les
 * achats (commandes, bons, rétrocessions).
 * ⚠️ LA FACETTE CHANGE, PAS SEULEMENT L’ÉTIQUETTE. Renommer le libellé en continuant de filtrer les
 * fournisseurs aurait été le mensonge type : un écran qui annonce une chose et en fait une autre,
 * sans erreur pour le trahir (§23.0). La clé `marq` voyage jusqu'au serveur, qui la connaît déjà.
 * ⚠️ SECTEUR et ACTIF/INACTIF restent RETIRÉS (2026-08-16) : ajoutés de ma propre initiative sans
 * relever la barre d'outils. Le serveur sait toujours les appliquer — capacité gardée, contrôle non
 * inventé. Secteur est de toute façon vide en base (trou de reprise daté).
 */
// ⚠️ NI LIBELLÉ NI CLÉ DE CHAMP DANS CETTE TABLE : le mot se lit AU RENDU par `facetLabel(d.key)`, et
//    la correspondance facette → champ vit UNE fois, dans `FACET_FIELD` (vocabulary.ts). Un mot lu à
//    l'import figerait « Saison » avant la lecture du magasin — le caviste ne verrait jamais
//    « Millésime », sans erreur pour le dire ; une correspondance recopiée ici divergerait de celle des
//    autres écrans au premier ajout.
// ⚠️ SAUF `tout`, ET C'EST UNE DETTE ASSUMÉE, PAS UN OUBLI. « Toutes les saisons » recopie le mot que
//    `facetLabel` résout juste à côté : un caviste qui renomme Saison en Millésime lira encore
//    « Toutes les saisons » sous une colonne « Millésime ». Le dériver demanderait `t('Toutes les
//    {champ}', { champ })`, qui annoncerait « Toutes les rayons » — **l'accord ne se calcule pas**, et
//    le dictionnaire de vocabulaire ne porte AUCUN genre aujourd'hui. Il s'ajoute en Console, avec le
//    mot ; d'ici là cette phrase reste juste, et elle se traduit.
const DIMENSIONS: { key: ArticleFacet; tout: Translatable }[] = [
  { key: 'fam', tout: key('Toutes les familles') },
  { key: 'marq', tout: key('Toutes les marques') },
  { key: 'sais', tout: key('Toutes les saisons') },
];

/**
 * L'état des filtres relu depuis les réglages mémorisés — les seules dimensions que l'écran offre.
 * ⚠️ Une dimension retirée (fournisseur, secteur) n'a plus de puce pour se révoquer : la relire la
 *    ferait voyager, invisible, avec la portée des écritures de masse.
 */
/**
 * LE COMPTE RENDU D'UNE ÉCRITURE DE MASSE — « 3 déclinaisons modifiées sur 2 modèles. »
 * ⚠️ DEUX COMPTES, DONC DEUX CLÉS COMPTÉES passées en valeur : le moteur n'accorde que `{n}`. La phrase recevait
 *    les deux nombres déjà formatés, et affichait « 1 déclinaisons modifiées sur 1 modèles. » dans le cas le plus
 *    courant — une seule fiche retouchée (Forme DD).
 */
export function writtenSummary(variants: number, models: number): string {
  return t('{variants} sur {modeles}.', { variants: t('{n} déclinaison modifiée', { n: variants }), modeles: t('{n} modèle', { n: models }) });
}

/** Le refus d'une sélection trop large : ses deux comptes accordés, et le plafond, qui n'accorde aucun mot. */
export function tooWideMessage(variants: number, models: number, max: number): string {
  return t('Cette sélection touche {variants} ({modeles}) : le maximum est de {max} par opération.', {
    variants: t('{n} déclinaison', { n: variants }), modeles: t('{n} modèle', { n: models }), max: nombre(max),
  });
}

export function restoredFacets(saved: Readonly<Record<string, string | undefined>>): Partial<Record<ArticleFacet, string>> {
  return Object.fromEntries(DIMENSIONS.map((d) => [d.key, saved[d.key] ?? '']));
}

/** Les filtres posés, sous la forme que le serveur attend : uniquement des dimensions que l'écran montre. */
export function offeredFilters(facets: Partial<Record<ArticleFacet, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of DIMENSIONS) {
    const v = facets[d.key];
    if (v) out[d.key] = v;
  }
  return out;
}

/**
 * LE FILTRE COURANT, sous la forme que la portée d'une écriture de masse envoie au serveur — tout ce qui filtre la
 * liste, et rien d'autre.
 * ⚠️ CHAQUE FILTRE DE LA LISTE Y PASSE : un filtre affiché mais absent de la portée ferait écrire sur des modèles que
 *    l'écran ne montrait pas — sans une erreur pour le trahir. « Tailles 2 uniquement » en était absent.
 */
export function scopeFilters(f: {
  search: string;
  facets: Partial<Record<ArticleFacet, string>>;
  secondAxis: boolean;
  importBatch: string | null;
}): Record<string, string> {
  return {
    ...(f.search ? { q: f.search } : {}),
    ...offeredFilters(f.facets),
    ...(f.secondAxis ? { axes: '2' } : {}),
    ...(f.importBatch ? { imp: f.importBatch } : {}),
  };
}

/** Ce que dit la pastille d'un lot importé : le nom du fichier et ses modèles. */
export const lotLabel = (b: ImportBatch): string => t('Import « {fichier} » · {n} article', { fichier: b.fileName, n: b.models });

/** Le nom sous lequel les réglages de CET écran sont mémorisés. Le changer perd les réglages en place. */
const TABLE_ID = 'bo-articles';

/** Taille de page par DÉFAUT (25, exploitation courante) — réglable à l'écran, plafonnée à 100 au serveur. */
const PAGE_SIZE = 25;

/**
 * Tri par défaut : la dernière écriture en tête.
 * ⚠️ IL N'A PAS DE COLONNE VISIBLE (la maquette le nomme au seul pied du tableau) — la colonne existe
 * pourtant, masquée par défaut et offerte au popover : sans elle, le pied ne saurait dire de QUOI on
 * est trié, et l'utilisateur ne pourrait jamais atteindre l'en-tête pour en changer.
 */
const DEFAULT_SORT: SortState = { key: 'modif', dir: 'desc' };

/** Réglage initial des colonnes : l'ordre déclaré, la seule « Dernière écriture » masquée. */
const DEFAULT_LAYOUT: ColumnLayout = { order: [], hidden: ['modif'] };

/** En-tête de page : le titre, et ce que la liste porte réellement de conséquences. */
function Heading({ total, filtre, menu }: { total: number | null; filtre: boolean; menu?: React.ReactNode }) {
  return (
    // ⚠️ LE BOUTON EST SUR LA LIGNE DU TITRE (relevé l.836 : `align-items:flex-end`), pas en dessous.
    // Posé sur sa propre rangée, il flottait au-dessus du tableau sans appartenir à rien — et il
    // poussait toute la liste de 52 px vers le bas.
    <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
      <div style={{ flex: 1, minWidth: 260 }}>
      <h1 style={{ fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-.025em', margin: 0, color: 'var(--ink-900)' }}>
        
        {t('Articles')}
      </h1>
      <p style={{ margin: '8px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.6, maxWidth: 760 }}>
        {/* ⚠️ Le nombre n'est affiché que lorsqu'il a été MESURÉ. Un « — modèles » en attendant serait
            moins clair, mais un chiffre affiché avant d'être connu serait faux. */}
        {/* ⚠️ `total` est le compte du jeu FILTRÉ — facettes et état actif compris —, pas du magasin :
            le serveur le mesure après application de la recherche. Écrit « dans ce magasin », il
            annonçait 12 modèles à un commerçant qui en a 3 000 dès qu'il tapait trois lettres. Le
            libellé suit donc l'état du filtre. */}
        {total === null
          ? t('Le catalogue de ce magasin')
          : filtre
            ? t('{n} modèle correspond aux filtres posés', { n: total })
            : t('{n} modèle', { n: total })}
        {/* ⚠️ RELEVÉ DE LA MAQUETTE (l.838), et ce n'est pas de la décoration : c'est la seule phrase de
            l'écran qui dise QUAND une modification atteint les caisses. Sans elle, on corrige un prix
            puis on va vérifier en caisse, où il n'a pas encore changé — et on croit à une panne.
            ⚠️ DEUX PHRASES ONT ÉTÉ RETIRÉES ICI. « Une ligne regroupe les déclinaisons… » décrivait le
            tableau, qui se lit tout seul. Et surtout « cet écran est en LECTURE : la création et la
            modification ne sont pas encore ouvertes » : c'était vrai quand je l'ai écrit, c'est FAUX
            depuis que la fiche écrit (prix, déclinaisons, photos). Une mention périmée ne se contente
            pas d'être inutile — elle dissuade d'essayer ce qui marche. */}
        {' · '}{t('le catalogue descend en lecture aux caisses au prochain contact')}
      </p>
      </div>
      {menu}
    </header>
  );
}

/**
 * Les colonnes du dessin, moins celles qui n'ont pas de donnée.
 *
 * ⚠️ LARGEURS ET LIBELLÉS REPRIS DU REGISTRE de la maquette (`COLDEF`), qui donne d'un seul tenant le
 * libellé, la largeur et la clé de tri : les recopier au jugé ferait diverger le popover, le `<colgroup>`
 * et la clé envoyée au serveur.
 * ⚠️ PAS DE COLONNE REMISE : aucune table de promotion n'existe dans la base magasin. Elle n'est pas
 * masquée, elle est ABSENTE — une colonne toujours à tiret mentirait par omission.
 * ⚠️ PA HT et MARGE SONT VISIBLES DE TOUS. La maquette les réserve au gérant, mais ce droit-là appartient
 * au modèle de rôles MÉTIER, qui vit dans la Console et n'est pas encore descendu au back-office (celui-ci
 * ne connaît que `bo.read`/`bo.write`). Inventer une permission ici la rendrait détenable par personne.
 */
/**
 * LA VIGNETTE D UN MODELE — celle que l etoile de l onglet Photos designe.
 *
 * ⚠️ SANS PHOTO, DES HACHURES ET NON UN BLANC (releve maquette l.933) : un carre vide se lit comme
 * une image qui n a pas fini de charger, et on attend. Les hachures disent « il n y en a pas », ce
 * qui est une information — et une invitation a en choisir une.
 *
 * ⚠️ ON NE RETOMBE PAS SUR LA PREMIERE PHOTO VENUE quand aucune n est marquee. Une image que
 * personne n a validee n a rien a faire en tete d un ecran commercial, et l absence de vignette est
 * precisement ce qui donne envie d aller en designer une.
 */
function Vignette({ url }: { url: string | null }) {
  const socle = { width: 34, height: 34, flexShrink: 0, borderRadius: 'var(--r-xs)' } as const;
  if (!url) {
    return (
      <div
        aria-hidden
        style={{
          ...socle,
          background: noPhotoHatchCss(),
        }}
      />
    );
  }
  return (
    <div
      role="img"
      aria-label={t('Visuel de l’article')}
      style={{
        ...socle, border: '1px solid var(--ink-200)',
        backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center',
      }}
    />
  );
}

/**
 * Les colonnes du journal. `axesParNom` : les grilles SECONDAIRES du magasin, pour la colonne « Tailles ».
 * Exportée pour la preuve comportementale : ses libellés se lisent à l'APPEL, avec le vocabulaire du moment.
 */
export function buildColumns(axesParNom: ReadonlyMap<string, SecondDimension>): ColumnDef<ArticleModel>[] {
  return [
    {
      key: 'designation',
      label: fieldLabel('name'),
      // Identité de la ligne : ni masquable, ni déplaçable — la lire ailleurs qu'en tête rendrait le
      // tableau illisible sans que rien ne l'annonce.
      locked: true,
      pinned: 'start',
      sortable: true,
      defaultSortDir: 'asc',
      // ⚠️ AUCUN SOCLE, ET C'EST VOULU : c'est la colonne qui ABSORBE la place restante (la maquette la
      // déclare `minmax(150px, 2.2fr)` — une élasticité bornée, pas une largeur). Son plancher est
      // déclaré ici : sans lui, une colonne sans largeur comptait zéro et recevait réellement 0 px
      // sous une fenêtre étroite — son texte s'enroulait caractère par caractère.
      // 220 et non 150 : elle porte un titre ET une sous-ligne mono ; sous 220 px l'ellipse mange la
      // sous-ligne entière.
      minWidth: 263,
      // La vignette compte dans le plancher : 34 px + sa gouttiere, sinon le texte perd d autant.
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <Vignette url={r.mainPhotoUrl} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {r.name}
            {/* Un modèle entièrement désactivé reste dans la liste (l'historique de vente le référence)
                mais il se DIT : sinon il se lit comme un article vendable. */}
            {r.activeCount === 0 && <Badge tone="neutral" size="sm" radius="ctrl" style={{ marginLeft: 8 }}>{t('Inactif')}</Badge>}
          </div>
          {/* ⚠️ LE CODE ARTICLE SE COPIE, comme le gencode (demande utilisateur 2026-08-25 : « partout
              où j'ai un gencode ou un code article je dois avoir l'option de copier »). C'est un
              identifiant qu'on RECOPIE ailleurs — dans un mail au fournisseur, dans un tableur, dans
              une recherche : le relire à l'œil est exactement l'occasion de se tromper d'un chiffre. */}
          <div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            {r.articleCode
              ? (
                <Copyable value={r.articleCode} label={t('Copier le code article {code}', { code: r.articleCode })} style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.articleCode}</span>
                </Copyable>
              )
              : <span>{t('sans code article')}</span>}
            <span>{` · ${t('{n} décl.', { n: r.variantCount })}`}</span>
          </div>
        </div>
        </div>
      ),
    },
    {
      // ⚠️ ELLE N'ÉLIT PLUS RIEN (arbitrage utilisateur, 2026-08-16). Elle affichait `min(gencode)` —
      // le premier par ordre alphabétique parmi douze — avec une pastille « +N » qui signalait les
      // autres sans les donner. C'est la faute qu'on refuse ailleurs : fourchette de prix plutôt qu'un
      // prix élu, marqueur « ≠ » plutôt qu'une famille élue. Un gencode appartient à la DÉCLINAISON.
      // Un modèle à code UNIQUE l'affiche (il est vrai, et copiable) ; au-delà, elle COMPTE — un compte
      // est vrai, une élection ne l'est pas — et le dépliage porte les codes.
      key: 'ean', label: fieldLabel('gencode'), width: 132, maxWidth: 190, mono: true, sortable: true,
      headerTitle: t('Un modèle à code unique l’affiche ; au-delà, la colonne COMPTE et les codes se lisent au dépliage — chacun appartient à une déclinaison, pas au modèle.'),
      render: (r) => {
        if (!r.gencode) return <Absent />;
        // Un seul code : il EST celui du modèle. Copiable pour de vrai (la primitive porte le repli
        // `execCommand` et dit l'échec — en HTTP, `navigator.clipboard` n'existe même pas).
        if (r.gencodeCount <= 1) return <Copyable value={r.gencode} label={t('Copier le gencode {code}', { code: r.gencode })} />;
        return (
          <span style={{ color: 'var(--ink-600)' }} title={t('Les codes de ce modèle se lisent au dépliage : chacun appartient à une déclinaison.')}>
            {t('{n} code', { n: r.gencodeCount })}
          </span>
        );
      },
    },
    // ⚠️ Socle + PLAFOND sur les colonnes de texte : elles s'élargissent jusqu'à leur contenu, puis
    // tronquent. Sans plafond, un libellé exceptionnel imposerait sa largeur à toute la colonne.
    { key: 'fam', label: fieldLabel('family'), width: 104, maxWidth: 200, locked: true, sortable: true, render: (r) => <Texte v={r.family} mixed={r.familyMixed} /> },
    { key: 'sect', label: fieldLabel('sector'), width: 86, maxWidth: 180, sortable: true, render: (r) => <Texte v={r.sector} mixed={r.sectorMixed} /> },
    {
      key: 'sais', label: fieldLabel('season'), width: 92, maxWidth: 190, sortable: true,
      // ⚠️ `seasonMixed` était CALCULÉ par le serveur et jamais affiché — les quatre autres colonnes de
      // classement montrent leur désaccord, celle-ci le taisait. Un modèle mi-Hiver mi-Été s'y lisait
      // comme un modèle d'une seule saison.
      render: (r) => (r.season
        ? <Badge tone="neutral" size="sm" radius="ctrl">{r.season}<Divergent on={r.seasonMixed} /></Badge>
        : <Absent />),
    },
    { key: 'marq', label: fieldLabel('brand'), width: 104, maxWidth: 200, sortable: true, render: (r) => <Texte v={r.brand} mixed={r.brandMixed} /> },
    {
      // ⚠️ LA COLONNE « TAILLES » de la maquette « Articles — Tailles 2 » : « 38 → 46 × 30/32/34 ». Première
      // et dernière viennent du serveur DANS L'ORDRE DE LA GRILLE ; le second axe vient de la grille que
      // LE MODÈLE lui a appariée. Un modèle sans grille montre ses tailles en texte, et le dit d'un « ≠ »
      // quand elles ne partagent pas la même grille.
      key: 'tailles', label: t('Tailles'), width: 132, maxWidth: 260, mono: true,
      headerTitle: t('Première → dernière taille dans l’ordre de la grille, et les valeurs du second axe quand le modèle en porte un.'),
      render: (r) => {
        if (r.sizedCount === 0) return <span style={{ color: 'var(--ink-400)' }}>{t('sans taille')}</span>;
        const dimension = r.sizeGrid2 ? axesParNom.get(r.sizeGrid2) ?? null : null;
        // Le drapeau du serveur fait foi : un second axe dont les valeurs n'ont pas pu être lues se dit quand même.
        // ⚠️ AUCUN SUFFIXE DE TEXTE ICI : la pastille le dit déjà, et mieux — elle ne se tronque pas
        //    quand la cellule est étroite, là où le texte perd sa fin en premier. Deux façons de dire
        //    la même chose dans la même cellule, c'est une de trop (forme A en miniature).
        const label = sizeSpanLabel(r.sizeFirst, r.sizeLast, dimension) ?? '';
        return (
          <span title={r.sizeGrid
            ? (r.sizeGrid2
              ? t('Grille « {grille} » × « {grille2} »', { grille: r.sizeGrid, grille2: r.sizeGrid2 })
              : t('Grille « {grille} »', { grille: r.sizeGrid }))
            : t('Aucune grille rattachée : tailles en texte')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, color: r.sizeGrid ? 'var(--ink-800)' : 'var(--ink-500)' }}>
            <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            {/* ⚠️ LA DOUBLE DIMENSION SE VOIT AVANT DE SE LIRE (relevé de l'étude « Articles — Tailles 2 » :
                « la colonne annonce la double dimension SANS L'ÉTALER — bornes × longueurs et badge T×L »).
                Une cellule tronquée perd d'abord sa fin, donc justement les valeurs du second axe ; la
                pastille, elle, ne se tronque pas et porte la couleur que la charte réserve à la GAMME.
                C'est aussi ce qui rend lisible d'un coup d'œil le filtre « Tailles 2 uniquement ». */}
            {r.secondAxis && (
              <Badge tone="violet" size="xs" form="code" title={t('Ce modèle se décline sur DEUX axes : tailles × second axe.')} style={{ flexShrink: 0 }}>
                
                {t('T×L')}
              </Badge>
            )}
            <Divergent on={r.sizeGridMixed} />
          </span>
        );
      },
    },
    {
      // ⚠️ `rigid` : une colonne de montants qui change de largeur d'une page à l'autre se compare mal.
      key: 'pa', label: fieldLabel('purchasePriceExclTax'), width: 80, rigid: true, numeric: true, mono: true, sortable: true,
      headerTitle: t('{champ} — hors taxes, stocké à l’échelle fine (4 décimales), AFFICHÉ au centime.', { champ: fieldLabel('purchasePriceExclTax') }),
      // ⚠️ `displayScale: 2` : le SPEC arrondit au centime À L'AFFICHAGE. Les 4 décimales existent pour
      // ne rien perdre au stockage (des articles ont un revient au sous-centime), pas pour être lues.
      render: (r) => <Fourchette min={r.costMinFine} max={r.costMaxFine} rendu={(v) => formatMoney(v, { scale: 4, displayScale: 2, thousandsSep: ' ' })} />,
    },
    {
      // ⚠️ LE HT ET LA TVA VIENNENT DU SERVEUR, déclinaison par déclinaison (l'arrondi de `htFromTtc`, celui de la marge) :
      //    refaits ici depuis la fourchette TTC et un taux, ils seraient faux sur un modèle à taux mêlés.
      key: 'pvht', label: t('Prix de vente HT'), width: 150, rigid: true, numeric: true, sortable: true,
      headerTitle: t('Le prix de vente TTC sans sa TVA, au taux de chaque déclinaison VENDABLE.'),
      render: (r) => (r.priceExclTaxMinCents === null
        ? <Absent />
        : <Fourchette min={r.priceExclTaxMinCents} max={r.priceExclTaxMaxCents ?? r.priceExclTaxMinCents} rendu={(v) => formatMoney(v, { thousandsSep: ' ' })} />),
    },
    {
      key: 'tva', label: t('Montant TVA'), width: 116, rigid: true, numeric: true, sortable: true,
      headerTitle: t('La TVA contenue dans le prix de vente TTC, au taux de chaque déclinaison VENDABLE.'),
      render: (r) => (r.taxMinCents === null
        ? <Absent />
        : <Fourchette min={r.taxMinCents} max={r.taxMaxCents ?? r.taxMinCents} rendu={(v) => formatMoney(v, { thousandsSep: ' ' })} />),
    },
    {
      // Socle élargi : « Prix de vente TTC » en capitales sans retour à la ligne ne tient pas dans 84 px,
      // et une colonne rigide n'a aucun recours au-delà de son socle.
      key: 'pv', label: fieldLabel('salePriceInclTax'), width: 160, rigid: true, numeric: true, locked: true, sortable: true,
      headerTitle: t('Fourchette mesurée sur les déclinaisons VENDABLES du modèle (les retirées ne comptent pas), indépendamment des filtres posés.'),
      render: (r) => (
        <strong style={{ color: 'var(--ink-900)', fontWeight: 700 }}>
          <Fourchette min={r.priceMinCents} max={r.priceMaxCents} rendu={(v) => formatMoney(v, { thousandsSep: ' ' })} />
        </strong>
      ),
    },
    {
      key: 'marge', label: t('Marge'), width: 70, rigid: true, numeric: true, sortable: true,
      // ⚠️ CALCULÉE ICI, et le dire est une obligation : (PV HT − PA) ÷ PV HT. Le champ `margin_bp`
      // de la base n'est écrit par personne — il valait 0, et l'écran affichait « 0,0 % » partout,
      // c'est-à-dire une donnée fabriquée rendue comme une mesure.
      headerTitle: t('CALCULÉE ICI depuis le prix de vente TTC, le taux de TVA et le prix d’achat : (PV HT − PA) ÷ PV HT. Vide si la TVA ou le prix d’achat manque — jamais 0 %.'),
      render: (r) => (r.marginMinBp === null
        ? <Absent />
        : (
          // Une marge NÉGATIVE n'est pas une anomalie de calcul : l'article se vend sous son prix
          // d'achat. C'est précisément ce qu'un gérant doit voir, donc ça se signale.
          <span style={{ color: (r.marginMinBp ?? 0) < 0 ? 'var(--danger-ink)' : undefined }}>
            <Fourchette min={r.marginMinBp} max={r.marginMaxBp ?? r.marginMinBp} rendu={(v) => `${formatMinor(v, { scale: 2, displayScale: 1 })} %`} />
          </span>
        )),
    },
    {
      key: 'stock', label: t('Stock'), width: 64, rigid: true, numeric: true, locked: true, sortable: true,
      headerTitle: t('Somme des quantités des déclinaisons, pour ce magasin. Un 0 en gris clair signale qu’aucun mouvement n’a jamais été enregistré — ce n’est pas une rupture constatée. Indicative : l’autorité est le serveur.'),
      render: (r) => <Stock qty={r.stockQty} />,
    },
    {
      key: 'modif', label: t('Dernière écriture'), width: 128, numeric: true, sortable: true,
      // ⚠️ LE LIBELLÉ DIT LE VRAI. `updated_at` est aussi le curseur de delta de la synchronisation :
      // une descente de catalogue complète le remet à jour sur toute la base. Ce n'est donc PAS une
      // « dernière modification » au sens métier, et l'appeler ainsi promettrait plus que la colonne ne tient.
      headerTitle: t('Date de la dernière ÉCRITURE de la ligne (curseur de synchronisation), pas d’une modification métier.'),
      render: (r) => (r.updatedAt ? <span className="mono">{new Date(r.updatedAt).toLocaleDateString('fr-FR')}</span> : <Absent />),
    },
  ];
}
